import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  Btns,
  ModalHead,
  ModalHeadLeft,
  TextField,
  Inputs,
  OverLayDiv,
  Textarea,
  HeadTitle,
  ButtonDiv,
  UploadDiv,
  TaskTitle,
  DropdownWrapper
} from './styles';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import { isMediaRecordingSupported, buildTaskDescriptionImageFolder } from '../../../helpers/common';
import { rootStore } from '../../../mobx/rootStore';
import { RawFile } from '../../../mobx/interfaces/task';
import { ProjectModel } from '../../../mobx/models/project';
import Modal from '../modal';
import DeleteModal from '../deleteModal';
import SnapRecordModal from '../snapRecordModal';
import Drawer from '../../customDrawer';
import Button from '../../button';
import IconButton from '../../button/iconButton';
import { Dropdown, DropdownItem } from '../../dropdown';
import CommentDropdown from '../../dropdowns/commentDropdown';
import DescriptionInputBox from '../../inputBox/descriptionInputBox';
import RenderProjectListV2 from './renderProjectList';
import RenderExistingFileV2 from '../../renderExistingFile';
import TaskFormFields from './TaskFormFields';
import { useCreateTaskForm } from './hooks/useCreateTaskForm';
import { useCreateTaskSubmit } from './hooks/useCreateTaskSubmit';
import { TaskModalContext } from '../../../interfaces/CreateTaskModalInterface';

interface Props {
  onCancel: () => void;
  loadData?: () => void;
  onProjectChange?: (value: boolean) => void;
  context?: TaskModalContext;
}

const CreateTaskModal = ({ onCancel, loadData, onProjectChange, context = { source: 'shortcut' } }: Props) => {
  const { taskStore, projectStore, settingStore, appStore, workspaceStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { project } = createTaskInput;
  const { uploadFilePercent } = projectStore;
  const { taskTemplateList } = rootStore.projectTemplateStore;
  const { subscriptionList } = settingStore;
  const { workspaceTotalTask } = workspaceStore;

  // Hooks
  const {
    loading,
    setLoading,
    fileUploading,
    setFileUploading,
    isEstimateDropdownOpen,
    setIsEstimateDropdownOpen,
    isAssignDropdownOpen,
    setIsAssignDropdownOpen,
    closeModel,
    attachment,
    selectFile,
    setSelectFile,
    isSnapModalOpen,
    labelsVersion,
    priorityVersion,
    fileInputRef,
    inlineRef,
    titleTextareaRef,
    clearData,
    closeConfirmModal,
    confirmDiscard,
    closeAttachmentDrawer,
    handleOpenSnapModal,
    handleCloseSnapModal,
    handleFileInput,
    openAttachmentPage,
    resizeTitleTextarea
  } = useCreateTaskForm(onCancel);

  const {
    isTemplateProject,
    isRecurringTask,
    loadProjectData,
    onCreateTask,
    onUpdateStatus,
    onFileUpload,
    onSelectFile,
    handleDocumentIconClick,
    milestoneItems
  } = useCreateTaskSubmit({
    context,
    onCancel,
    loadData,
    onProjectChange,
    inlineRef,
    setLoading,
    setFileUploading
  });

  // Mount the file picker on open (it fetches files on mount) and keep it for the close animation,
  // but only for the project it was opened with so switching project doesn't fetch while it's closed.
  const attachmentProjectIdRef = useRef<string>();
  if (attachment) attachmentProjectIdRef.current = project?.id;
  const shouldRenderFilePicker = !!project?.id && attachmentProjectIdRef.current === project.id;

  const taskData =
    context.source === 'recurring' || context.source === 'myTask' || context.source === 'taskDetail'
      ? context.task
      : undefined;

  // Effects
  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  useEffect(() => {
    if (
      subscriptionList?.activeSubscription &&
      subscriptionList?.activeSubscription?.length === 0 &&
      workspaceTotalTask >= 250
    ) {
      appStore.setIsPlanUpgradeModelOpen(true);
      onCancel();
    }
  }, [appStore, onCancel, subscriptionList, workspaceTotalTask]);

  // Mobile file picker shortcut
  if (selectFile) {
    return (
      <RenderExistingFileV2
        setAttachment={() => closeAttachmentDrawer()}
        setSelectFile={(value: boolean) => setSelectFile(value)}
        onSelectFile={onSelectFile}
        isCreateTaskModal={true}
        removeMargin={true}
      />
    );
  }

  // Template dropdown items
  const templateData = taskTemplateList?.map((template) => ({
    key: template?.id || '',
    label: template?.templateName || ''
  }));

  return (
    <>
      {/* Header */}
      <ModalHead>
        <ModalHeadLeft>
          <RenderProjectListV2 isTemplateProject={isTemplateProject} />
          {project?.isMilestone && project?.milestones?.length > 0 && (
            <>
              <SVGIcon
                name='dot-icon'
                viewBox='0 0 15 15'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                className='dot-icon'
              />
              <Dropdown
                modalTitle='Milestone'
                content={
                  <HeadTitle>
                    {createTaskInput?.milestoneId ? createTaskInput?.milestone?.milestoneName : 'Select Milestone'}
                  </HeadTitle>
                }
                trigger='click'>
                {milestoneItems?.map((m) => (
                  <DropdownItem key={m.id} label={m.label} onClick={m.onClick} />
                ))}
              </Dropdown>
            </>
          )}
        </ModalHeadLeft>
        <IconButton
          iconName='new-close-icon'
          variant={ICON_BUTTON_TYPE.TERTIARY}
          size={ICON_BUTTON_SIZE.SMALL}
          onClick={clearData}
        />
      </ModalHead>

      {/* Body */}
      <Inputs>
        <TextField>
          <TaskTitle
            ref={titleTextareaRef}
            className='taskArea'
            placeholder='Task Title'
            autoFocus={true}
            value={createTaskInput?.name}
            onChange={(e) => {
              taskStore.createTaskInput.setName(e.target.value);
              resizeTitleTextarea();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                inlineRef.current?.focus();
              }
            }}
          />
        </TextField>
        <Textarea>
          <DescriptionInputBox
            ref={inlineRef}
            valueContent={createTaskInput?.description!}
            setValueContent={(value: string) => taskStore.createTaskInput.setDescription(value)}
            uploadedFiles={createTaskInput?.files || []}
            setUploadedFiles={(value: RawFile[]) => createTaskInput.setFiles(value)}
            setPropsLoading={(value: boolean) => setLoading(value)}
            setStateLoading={(value: boolean) => setFileUploading(value)}
            handleOpenSnapModal={handleOpenSnapModal}
            handleFileInput={handleFileInput}
            uploadFolder={buildTaskDescriptionImageFolder(createTaskInput?.project?.id)}
          />
        </Textarea>
      </Inputs>
      {/* file uploading indicator */}
      {fileUploading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}

      {/* Footer */}
      <Btns>
        <TaskFormFields
          project={project as ProjectModel}
          isTemplateProject={isTemplateProject}
          isRecurringTask={isRecurringTask}
          labelsVersion={labelsVersion}
          priorityVersion={priorityVersion}
          currentStatusId={createTaskInput?.statusId}
          groupId={createTaskInput?.groupId}
          setIsAssignDropdownOpen={setIsAssignDropdownOpen}
          onUpdateStatus={onUpdateStatus}
          loadData={loadData}
          data={taskData}
        />
        <ButtonDiv>
          <input type='file' ref={fileInputRef} hidden onChange={onFileUpload} multiple aria-label='Upload file' />
          <DropdownWrapper>
            {!createTaskInput?.id && taskTemplateList?.length > 0 && (
              <Dropdown
                modalTitle='Template'
                content={
                  <IconButton
                    iconName='document-icon'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                  />
                }
                trigger='click'>
                {templateData.map((item) => (
                  <DropdownItem key={item.key} label={item.label} onClick={() => handleDocumentIconClick(item.key)} />
                ))}
              </Dropdown>
            )}
            <Dropdown
              modalTitle='Attachment'
              activeClassName='active'
              content={
                <IconButton
                  iconName='file-attachment-icon'
                  size={ICON_BUTTON_SIZE.MEDIUM}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                />
              }
              trigger='click'>
              <CommentDropdown
                onClickUploadFile={handleFileInput}
                onClickSelectFile={openAttachmentPage}
                isTemplateProject={isTemplateProject}
              />
            </Dropdown>
          </DropdownWrapper>
          <Button
            onClick={onCreateTask}
            title={createTaskInput?.id ? 'Update Task' : 'Create Task'}
            isLoading={loading || fileUploading}
            size={'small'}
            variant={BUTTON_TYPE.PRIMARY}
          />
          <Drawer placement='right' onClose={closeAttachmentDrawer} open={attachment} width='350px'>
            {shouldRenderFilePicker && (
              <RenderExistingFileV2
                setAttachment={closeAttachmentDrawer}
                onSelectFile={onSelectFile}
                isCreateTaskModal={true}
              />
            )}
          </Drawer>
        </ButtonDiv>
      </Btns>

      {/* Overlays */}
      {isEstimateDropdownOpen && (
        <OverLayDiv role='button' className='dropdown-overlay' onClick={() => setIsEstimateDropdownOpen(false)} />
      )}
      {isAssignDropdownOpen && (
        <OverLayDiv role='button' className='dropdown-overlay' onClick={() => setIsAssignDropdownOpen(false)} />
      )}

      {/* Modals */}
      <Modal open={closeModel} onClose={closeConfirmModal} maxWidth={'334px'}>
        <DeleteModal
          isYes={true}
          onClose={closeConfirmModal}
          onOk={confirmDiscard}
          modalTitle='Confirmation Model'
          description='Are you sure you want close this model?'
        />
      </Modal>
      {isMediaRecordingSupported() && (
        <Modal open={isSnapModalOpen} onClose={handleCloseSnapModal} maxWidth={'258px'}>
          <SnapRecordModal handleCloseModal={handleCloseSnapModal} />
        </Modal>
      )}
    </>
  );
};

export default observer(CreateTaskModal);
